/*======================================================================================================================
  * Bluebell: src/app/state/assembly-libraries/assembly-library.reducer.ts
  * Last Edited: 14/02/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { createEntityAdapter, EntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { AssemblyLibraryActions } from '@state/assembly-libraries/assembly-library.actions';
import { AssemblyFull, AssemblyLibrary } from '@state/assembly-libraries/assembly-library.interfaces';


export interface State extends EntityState<AssemblyLibrary> {
  loading: boolean;
  loaded: boolean;
  loadInactive: boolean;
  inactiveLibrariesLoaded: boolean;
  expandedLibraries: string[];
  searchString: string|null;
  selectedAssembly: AssemblyFull|null;
  selectedAssemblyLoading: boolean;
}

export const adapter: EntityAdapter<AssemblyLibrary> = createEntityAdapter<AssemblyLibrary>({
  selectId: (library: AssemblyLibrary) => library.uuid,
});

export const initialState: State = adapter.getInitialState<State>({
  loading: false,
  loaded: false,
  loadInactive: false,
  inactiveLibrariesLoaded: false,
  expandedLibraries: [],
  searchString: null,
  selectedAssembly: null,
  selectedAssemblyLoading: false,
});

export const reducer = createReducer(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return adapter.removeAll({...initialState});
  }),

  on(AssemblyLibraryActions.createSuccess, (state, { library }) => {
    return adapter.setAll([library, ...getAll(state)], state);
  }),

  on(AssemblyLibraryActions.retrieve, (state) => {
    return {
      ...state,
      loading: true,
    };
  }),

  on(AssemblyLibraryActions.retrieveSuccess, (state, { libraries }) => {
    return adapter.setAll(
      libraries.toSorted((a, b) => a.name.localeCompare(b.name)),
      {
        ...state,
        loading: false,
        loaded: true,
        inactiveLibrariesLoaded: state.inactiveLibrariesLoaded || state.loadInactive,
      }
    );
  }),

  on(AssemblyLibraryActions.retrieveFailure, (state) => {
    return {
      ...state,
      loading: false,
    };
  }),

  on(AssemblyLibraryActions.updateSuccess, (state, { library }) => {
    return adapter.upsertOne(library, state);
  }),

  on(AssemblyLibraryActions.deleteSuccess, (state, { uuid }) => {
    return adapter.removeOne(uuid, state);
  }),



  //

  on(AssemblyLibraryActions.toggleLoadInactive, (state) => {
    return {
      ...state,
      loadInactive: !state.loadInactive,
    };
  }),

  on(AssemblyLibraryActions.setSearchString, (state, { searchString }) => {
    return {
      ...state,
      searchString,
    };
  }),

  //

  on(AssemblyLibraryActions.setExpanded, (state, { expandedLibraries }) => {
    return {
      ...state,
      expandedLibraries,
    };
  }),

  on(AssemblyLibraryActions.expandLibrary, (state, { uuid }) => {
    return {
      ...state,
      expandedLibraries: [
        ...state.expandedLibraries.filter(libraryUUID => libraryUUID !== uuid),
        uuid
      ]
    };
  }),

  on(AssemblyLibraryActions.collapseLibrary, (state, { uuid }) => {
    return {
      ...state,
      expandedLibraries: state.expandedLibraries.filter(libraryUUID => libraryUUID !== uuid)
    };
  }),

  on(AssemblyLibraryActions.expandAll, (state) => {
    return {
      ...state,
      expandedLibraries: state.ids as string[],
    };
  }),

  on(AssemblyLibraryActions.collapseAll, (state) => {
    return {
      ...state,
      expandedLibraries: initialState.expandedLibraries,
    };
  }),


  /*
    Assembly Actions
  */

  on(
    AssemblyLibraryActions.createAssemblySuccess,
    AssemblyLibraryActions.cloneAssemblySuccess,
    (state, { assembly }) => {
      const id = assembly.library;
      const entity = state.entities[id];
      const update = {
        id,
        changes: {
          parts: [assembly, ...(entity?.parts || [])]
        }
      };
      return adapter.updateOne(update, state);
    }
  ),

  on(AssemblyLibraryActions.selectAssembly, (state) => {
    return {
      ...state,
      selectedAssemblyLoading: true,
      selectedAssembly: null,
    };
  }),

  on(AssemblyLibraryActions.selectAssemblySuccess, (state, { selectedAssembly }) => {
    return {
      ...state,
      selectedAssembly,
      selectedAssemblyLoading: false
    };
  }),

  on(
    AssemblyLibraryActions.selectAssemblyFailure,
    AssemblyLibraryActions.clearSelectedAssembly,
    (state) => {
      return {
        ...state,
        selectedAssembly: null,
        selectedAssemblyLoading: false,
      };
    }
  ),

  on(
    AssemblyLibraryActions.updateAssemblySuccess,
    AssemblyLibraryActions.updateAssemblyPartsSuccess,
    (state, { selectedAssembly, currentLib }) => {
    const library = state.entities[selectedAssembly.library];

    const update: Update<AssemblyLibrary> = {
      id: library?.uuid as string,
      changes: {
        parts: [selectedAssembly, ...(library?.parts || []).filter(part => part.uuid !== selectedAssembly.uuid)]
      }
    };

    let updatedState = adapter.updateOne(update, state);

    if (currentLib && currentLib !== selectedAssembly.library) {

      const currentLibrary = state.entities[currentLib];

      const updateCurrent: Update<AssemblyLibrary> = {
        id: currentLibrary?.uuid as string,
        changes: {
          parts: [...(currentLibrary?.parts || []).filter(part => part.uuid !== selectedAssembly.uuid)]
        }
      };

      updatedState = adapter.updateOne(updateCurrent, updatedState);
    }

    return {
      ...updatedState,
      selectedAssembly: state.selectedAssembly ? selectedAssembly : null,
    };
  }),

  on(AssemblyLibraryActions.deleteAssemblySuccess, (state, { uuid }) => {
    const updates: Update<AssemblyLibrary>[] = [];
    state.ids.forEach(key => {
      const entity: AssemblyLibrary|undefined = state.entities[key];
      if (entity?.parts.find(assembly => assembly.uuid === uuid)) {
        updates.push({
          id: entity.uuid,
          changes: {
            parts: entity.parts.filter(assembly => assembly.uuid !== uuid)
          }
        });
      }
    });
    if (updates.length) {
      return adapter.updateMany(updates, state);
    } else {
      return state;
    }
  }),

);

export const {
  selectIds: getIds,
  selectEntities: getEntities,
  selectAll: getAll,
  selectTotal: getTotal,
} = adapter.getSelectors();

export const featureKey = 'assemblyLibraries';
export const getLoading = (state: State) => state.loading;
export const getLoaded = (state: State) => state.loaded;
export const getLoadInactive = (state: State) => state.loadInactive;
export const getInactiveLibrariesLoaded = (state: State) => state.inactiveLibrariesLoaded;
export const getExpandedLibraries = (state: State) => state.expandedLibraries;
export const getSearchString = (state: State) => state.searchString;
export const getSelectedAssembly = (state: State) => state.selectedAssembly;
export const getSelectedAssemblyLoading = (state: State) => state.selectedAssemblyLoading;
